import React, { useState } from "react";
import { useSelector } from "react-redux";
import { IntlProvider } from "react-intl";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import translations from "app/libs/i18n/translations.json";
import { useTheme } from "@mui/styles";
import { Box } from "@mui/material";
import InternalLoginPage from "bundles/Internal/components/InternalLoginPage";
import CandidatesIndexPage from "bundles/Internal/components/CandidatesIndexPage";
import CvVaultPage from "bundles/Internal/components/CvVaultPage";
import CvTablePage from "bundles/Internal/components/CvTablePage";
import FrenchMarketCandidatesPage from "bundles/Internal/components/FrenchMarketCandidatesPage";
import ArchivedCandidatesPage from "bundles/Internal/components/ArchivedCandidatesPage";
import CandidateShowPage from "bundles/Internal/components/CandidateShowPage";
import InternalDashboardPage from "bundles/Internal/components/InternalDashboardPage";
import InternalProfilePipelinePage from "bundles/Internal/components/InternalProfilePipelinePage";
import ExternalProfilesIndexPage from "bundles/Internal/components/ExternalProfilesIndexPage";
import CoordinatorDashboard from "bundles/Internal/components/CoordinatorDashboard";
import TalentRankingsPage from "bundles/Internal/components/talent_rankings/TalentRankingsPage";
import CoordinatorPipelineTrackingPage from "bundles/Internal/components/coordinator/CoordinatorPipelineTrackingPage";
import PipelinesIndexPage from "bundles/Internal/components/PipelinesIndexPage";
import InternalAppBar from "bundles/Internal/components/InternalAppBar";
import InternalInvitationsPage from "bundles/Internal/components/InternalInvitationsPage";
import InternalAcceptInvitationPage from "bundles/Internal/components/InternalAcceptInvitationPage";
import AnnouncementModal from "bundles/Internal/components/common/AnnouncementModal";
import CandidateModalProvider from "bundles/Internal/components/common/CandidateModalProvider";
import FloatingTimer from "bundles/Internal/components/common/FloatingTimer";
import TimeTrackerPage from "bundles/Internal/components/TimeTrackerPage";
import RecruitersIndexPage from "bundles/Internal/components/RecruitersIndexPage";
import RecruiterShowPage from "bundles/Internal/components/RecruiterShowPage";
import CompaniesIndexPage from "bundles/Internal/components/CompaniesIndexPage";
import CompanyShowPage from "bundles/Internal/components/CompanyShowPage";
import JobOffersIndexPage from "bundles/Internal/components/JobOffersIndexPage";
import JobOfferShowPage from "bundles/Internal/components/JobOfferShowPage";
import JobApplicationsIndexPage from "bundles/Internal/components/JobApplicationsIndexPage";
import JobApplicationShowPage from "bundles/Internal/components/JobApplicationShowPage";
import CandidateInterestsIndexPage from "bundles/Internal/components/CandidateInterestsIndexPage";
import BlogPostsIndexPage from "bundles/Internal/components/BlogPostsIndexPage";
import BlogPostShowPage from "bundles/Internal/components/BlogPostShowPage";
import SuggestionsIndexPage from "bundles/Internal/components/SuggestionsIndexPage";
import CreditsIndexPage from "bundles/Internal/components/CreditsIndexPage";
import StatsPageView from "bundles/Internal/components/charts/StatsPageView";
import PlatformAnalyticsPage from "bundles/Internal/components/PlatformAnalyticsPage";
import InternalActivitiesIndexPage from "bundles/Internal/components/InternalActivitiesIndexPage";
import TimeProjectIndexPage from "bundles/Internal/components/time_project/TimeProjectIndexPage";
import TimeProjectShowPage from "bundles/Internal/components/time_project/TimeProjectShowPage";

const InternalContainer = () => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const theme = useTheme();
  const messages = translations[i18nLocale] || {};

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const drawerWidth = sidebarOpen ? 240 : 56;

  const isSignInPage = window.location.pathname.startsWith("/internal/sign_in");
  const isAcceptInvitationPage = window.location.pathname.startsWith("/internal/invitation/accept");

  return (
    <IntlProvider locale={i18nLocale} key={i18nLocale} messages={messages}>
      <BrowserRouter>
        <CandidateModalProvider>
          <Box
            className={theme.background}
            sx={{
              minHeight: "100dvh",
              display: "flex",
              flexDirection: "row",
            }}
          >
            {!isSignInPage && !isAcceptInvitationPage && (
              <InternalAppBar
                open={sidebarOpen}
                onToggle={() => setSidebarOpen((v) => !v)}
                drawerWidth={drawerWidth}
              />
            )}
            {!isSignInPage && !isAcceptInvitationPage && <AnnouncementModal />}
            {!isSignInPage && !isAcceptInvitationPage && <FloatingTimer />}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Routes>
                <Route path="/internal" element={<InternalDashboardPage />} />
                <Route path="/internal/dashboard" element={<InternalDashboardPage />} />
                <Route path="/internal/talent-rankings" element={<TalentRankingsPage />} />
                <Route path="/internal/coordinator-dashboard" element={<CoordinatorDashboard />} />
                <Route path="/internal/coordinator-pipelines/:id" element={<CoordinatorPipelineTrackingPage />} />
                <Route path="/internal/pipelines" element={<PipelinesIndexPage />} />
                <Route path="/internal/pipelines/:id" element={<InternalProfilePipelinePage />} />
                <Route path="/internal/sign_in" element={<InternalLoginPage />} />
                <Route path="/internal/candidates" element={<CandidatesIndexPage drawerWidth={drawerWidth} />} />
                <Route
                  path="/internal/candidates/to_be_reviewed"
                  element={<CandidatesIndexPage drawerWidth={drawerWidth} scope="to_be_reviewed" />}
                />
                <Route path="/internal/candidates/french_market" element={<FrenchMarketCandidatesPage />} />
                <Route path="/internal/candidates/archived" element={<ArchivedCandidatesPage />} />
                <Route path="/internal/cv-vault" element={<CvVaultPage />} />
                <Route path="/internal/cv-table" element={<CvTablePage />} />
                <Route path="/internal/candidates/:id" element={<CandidateShowPage />} />
                <Route path="/internal/external_profiles" element={<ExternalProfilesIndexPage />} />
                <Route path="/internal/recruiters" element={<RecruitersIndexPage />} />
                <Route path="/internal/recruiters/:id" element={<RecruiterShowPage />} />
                <Route path="/internal/companies" element={<CompaniesIndexPage />} />
                <Route path="/internal/companies/:id" element={<CompanyShowPage />} />
                <Route path="/internal/job_offers" element={<JobOffersIndexPage />} />
                <Route path="/internal/job_offers/:id" element={<JobOfferShowPage />} />
                <Route path="/internal/job_applications" element={<JobApplicationsIndexPage />} />
                <Route path="/internal/job_applications/:id" element={<JobApplicationShowPage />} />
                <Route path="/internal/blog_posts" element={<BlogPostsIndexPage />} />
                <Route path="/internal/blog_posts/:id" element={<BlogPostShowPage />} />
                <Route path="/internal/candidate_interests" element={<CandidateInterestsIndexPage />} />
                <Route path="/internal/suggestions" element={<SuggestionsIndexPage />} />
                <Route path="/internal/credits" element={<CreditsIndexPage />} />
                <Route path="/internal/platform-analytics" element={<PlatformAnalyticsPage />} />
                <Route path="/internal/stats/:id" element={<StatsPageView />} />
                <Route path="/internal/internal-activities" element={<InternalActivitiesIndexPage />} />
                <Route path="/internal/time-tracker" element={<TimeTrackerPage />} />
                <Route path="/internal/time-project" element={<TimeProjectIndexPage />} />
                <Route path="/internal/time-project/:id" element={<TimeProjectShowPage />} />
                <Route path="/internal/users/invitations" element={<InternalInvitationsPage />} />
                <Route path="/internal/invitation/accept" element={<InternalAcceptInvitationPage />} />
                <Route path="*" element={<InternalDashboardPage />} />
              </Routes>
            </Box>
          </Box>
        </CandidateModalProvider>
      </BrowserRouter>
    </IntlProvider>
  );
};

export default InternalContainer;

